import * as React from 'react';
import { Button, FormGroup, TextInput } from '@patternfly/react-core';

function isValidDate(value: string, today: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value > today) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

interface AsOfControlProps {
  asOf: string;
  today: string;
  onChange: (asOf: string | undefined) => void;
}

export const AsOfControl: React.FunctionComponent<AsOfControlProps> = ({
  asOf,
  today,
  onChange,
}) => {
  const [value, setValue] = React.useState(asOf);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setValue(asOf);
    setError(null);
  }, [asOf]);

  const submit = React.useCallback((event: React.FormEvent) => {
    event.preventDefault();
    if (!isValidDate(value, today)) {
      setError('Enter a real date in YYYY-MM-DD format that is not in the future.');
      return;
    }
    setError(null);
    onChange(value);
  }, [onChange, today, value]);

  const showToday = React.useCallback(() => {
    setValue(today);
    setError(null);
    onChange(undefined);
  }, [onChange, today]);

  return (
    <form className="risk-register__as-of-control" onSubmit={submit}>
      <FormGroup label="View as of" fieldId="risk-register-as-of">
        <div className="risk-register__as-of-input">
          <TextInput
            id="risk-register-as-of"
            type="date"
            value={value}
            max={today}
            onChange={(_event, next) => setValue(next)}
            aria-invalid={Boolean(error)}
          />
          <Button variant="secondary" type="submit">Apply date</Button>
          {asOf !== today && (
            <Button variant="link" type="button" onClick={showToday}>Show today</Button>
          )}
        </div>
        {error && <p className="risk-register__as-of-error" role="alert">{error}</p>}
      </FormGroup>
    </form>
  );
};
